import React, { useState, useEffect, useCallback } from 'react';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { useAlert } from '../context/AlertContext';
import { useSocket } from '../context/SocketContext';
import './Feedback.css';

function Feedback() {
  const { user } = useAuth();
  const { showAlert } = useAlert();
  const { socket } = useSocket();

  const [items, setItems] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [resolvedOpen, setResolvedOpen] = useState(false);

  const isAdmin = user?.role === 'admin';

  const loadFeedback = useCallback(async () => {
    try {
      const res = await axios.get('/api/feedback');
      setItems(res.data);
    } catch (e) {
      console.error('[Feedback] load error:', e);
    }
  }, []);

  useEffect(() => {
    loadFeedback();
  }, [loadFeedback]);

  useEffect(() => {
    if (!socket) return;
    socket.on('feedback:new', loadFeedback);
    return () => socket.off('feedback:new', loadFeedback);
  }, [socket, loadFeedback]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setLoading(true);
    try {
      await axios.post('/api/feedback', { message });
      setMessage('');
      showAlert('Feedback gespeichert!', 'success');
      loadFeedback();
    } catch (e) {
      showAlert('Fehler beim Speichern.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async (id) => {
    try {
      await axios.put(`/api/feedback/${id}/resolve`);
      showAlert('Als erledigt markiert.', 'success');
      loadFeedback();
    } catch (e) {
      showAlert('Fehler beim Aktualisieren.', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Feedback wirklich löschen?')) return;
    try {
      await axios.delete(`/api/feedback/${id}`);
      showAlert('Gelöscht.', 'success');
      loadFeedback();
    } catch (e) {
      showAlert('Fehler beim Löschen.', 'error');
    }
  };

  const formatDate = (ts) => {
    if (!ts) return '';
    return new Date(ts).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getPlatformBadge = (platform) => {
    switch (platform) {
      case 'twitch-main': return { icon: '💜', label: 'Twitch' };
      case 'twitch-247':  return { icon: '🔄', label: 'Twitch 24/7' };
      case 'youtube':     return { icon: '▶️', label: 'YouTube' };
      case 'discord':     return { icon: '💙', label: 'Discord' };
      default:            return { icon: '🌐', label: 'Web' };
    }
  };

  const openItems = items.filter(i => i.status === 'open');
  const resolvedItems = items.filter(i => i.status !== 'open');

  const renderCard = (item) => {
    const badge = getPlatformBadge(item.platform);
    const resolved = item.status !== 'open';
    return (
      <div key={item.id} className={`feedback-card${resolved ? ' resolved' : ''}`}>
        <div className="feedback-card-header">
          <div className="feedback-author">
            <span className="feedback-username">{item.author}</span>
            <span className="feedback-platform-badge">
              {badge.icon} {badge.label}
            </span>
          </div>
          <div className="feedback-meta">
            <span className="feedback-date">{formatDate(item.created_at)}</span>
            {resolved && item.resolved_at && (
              <span className="feedback-resolved-date">✓ {formatDate(item.resolved_at)}</span>
            )}
          </div>
        </div>
        <div className="feedback-message">{item.message}</div>
        {isAdmin && (
          <div className="feedback-actions">
            {!resolved && (
              <button className="btn-resolve" onClick={() => handleResolve(item.id)}>
                ✓ Erledigt
              </button>
            )}
            <button className="btn-delete" onClick={() => handleDelete(item.id)}>
              🗑️
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="feedback-page">
      <div className="feedback-header">
        <h1>🐛 Bugfixes &amp; Feedback Beta</h1>
        <p>Melde Bugs oder teile dein Feedback direkt hier oder per <code>!bfix</code> im Chat.</p>
      </div>

      <form className="feedback-form" onSubmit={handleSubmit}>
        <textarea
          className="feedback-textarea"
          placeholder="Dein Feedback oder Bug-Beschreibung..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={3}
        />
        <button className="btn-submit" type="submit" disabled={loading || !message.trim()}>
          {loading ? 'Senden...' : 'Feedback senden'}
        </button>
      </form>

      <section className="feedback-section">
        <h2 className="section-title">
          <span>Offen</span>
          <span className="section-count">{openItems.length}</span>
        </h2>
        {openItems.length === 0 ? (
          <p className="feedback-empty">Keine offenen Einträge.</p>
        ) : (
          openItems.map(renderCard)
        )}
      </section>

      <section className="feedback-section">
        <button
          className="section-toggle"
          onClick={() => setResolvedOpen(o => !o)}
        >
          <h2 className="section-title">
            <span>Erledigt</span>
            <span className="section-count">{resolvedItems.length}</span>
          </h2>
          <span className="toggle-icon">{resolvedOpen ? '▲' : '▼'}</span>
        </button>
        {resolvedOpen && (
          resolvedItems.length === 0 ? (
            <p className="feedback-empty">Keine erledigten Einträge.</p>
          ) : (
            resolvedItems.map(renderCard)
          )
        )}
      </section>
    </div>
  );
}

export default Feedback;
